import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const ProgressContext = createContext(null);
const GUEST_KEY = "fibula_guest_progress";
const EMPTY = { medication: {}, ekg: {}, stats: {} };

const normalize = (d) => ({ medication: d?.medication || {}, ekg: d?.ekg || {}, stats: d?.stats || {} });
const isEmpty = (p) => !Object.keys(p.medication).length && !Object.keys(p.ekg).length && !Object.keys(p.stats).length;

function readGuest() {
  try { return normalize(JSON.parse(localStorage.getItem(GUEST_KEY))); } catch { return EMPTY; }
}

export function ProgressProvider({ children }) {
  const { user } = useAuth();
  const [progress, setProgress] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);
  const modeRef = useRef("guest"); // guest | user

  useEffect(() => {
    if (user === null) return; // still checking session
    if (user === false) {
      modeRef.current = "guest";
      setProgress(readGuest());
      setLoaded(true);
      return;
    }
    (async () => {
      let next = EMPTY;
      try {
        const { data } = await api.get("/progress");
        next = normalize(data);
        const guest = readGuest();
        if (isEmpty(next) && !isEmpty(guest)) {
          next = guest;
          await api.put("/progress", next).catch(() => { /* noop */ });
          localStorage.removeItem(GUEST_KEY);
        }
      } catch { /* noop */ }
      modeRef.current = "user";
      setProgress(next);
      setLoaded(true);
    })();
  }, [user]);

  const persist = useCallback((next) => {
    if (modeRef.current === "guest") {
      localStorage.setItem(GUEST_KEY, JSON.stringify(next));
      return;
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/progress", next).catch(() => { /* noop */ });
    }, 600);
  }, []);

  const update = useCallback(
    (patch) => {
      setProgress((prev) => {
        if (typeof patch === "function") patch = patch(prev) || {};
        const next = {
          medication: patch.medication ?? prev.medication,
          ekg: patch.ekg ?? prev.ekg,
          stats: patch.stats ?? prev.stats,
        };
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const isGuest = user === false;

  return (
    <ProgressContext.Provider value={{ progress, update, loaded, isGuest }}>
      {children}
    </ProgressContext.Provider>
  );
}

export const useProgress = () => useContext(ProgressContext);
